// Cost budget query — reads daily spend vs cap from BE cost endpoint.
// Soft warn lets admins intervene before hard block; 80% threshold matches
// budget UX convention. Cap set per workflow or org-wide; FE only displays.
// If BE cost endpoint is not yet shipped, the feature-flag stub returns ok
// state and the banner/chip render nil — zero UX disruption.

import { useQuery } from "@tanstack/react-query";
import { hubCall } from "@/lib/api/hub";

export type CostBudgetState = "ok" | "warn" | "blocked";

export interface CostBudget {
  state: CostBudgetState;
  currentUsd: number;
  capUsd: number | null;
  percentUsed: number | null;
  /** True when a run was aborted due to cost cap being exceeded. */
  aborted: boolean;
}

interface CostBudgetResponse {
  data: CostBudget;
}

/** Stub returned when cost endpoint is not yet shipped. */
const STUB: CostBudget = {
  state: "ok",
  currentUsd: 0,
  capUsd: null,
  percentUsed: null,
  aborted: false,
};

async function fetchCostBudget(
  resourceType: string,
  resourceId: string
): Promise<CostBudget> {
  // The shipped BE exposes aggregate cost endpoints under /cost/budget.
  // Workflow-scoped caps are not available yet, so avoid polling a missing
  // /cost-budgets/:type/:id endpoint and keep the optional chip hidden.
  if (resourceType === "workflow" || resourceType === "workflow_run") {
    return STUB;
  }

  try {
    const res = await hubCall<CostBudgetResponse>(
      `/cost-budgets/${resourceType}/${resourceId}`
    );
    return res.data;
  } catch (err) {
    // Cost endpoint may not be deployed; surface stub so banner is hidden.
    const status = (err as { status?: number }).status;
    if (status === 404 || status === 501) return STUB;
    throw err;
  }
}

function costBudgetKey(resourceType: string, resourceId: string) {
  return ["cost-budget", resourceType, resourceId] as const;
}

export interface UseCostBudgetOptions {
  resourceType: string;
  resourceId: string;
  /** Defaults to true. Set false to skip fetching (e.g. when resourceId unknown). */
  enabled?: boolean;
}

/**
 * Reads today's cost spend vs cap. Refetches every 60s as a background poll
 * plus on window focus so the banner state stays reasonably current without SSE.
 */
export function useCostBudget({
  resourceType,
  resourceId,
  enabled = true,
}: UseCostBudgetOptions) {
  return useQuery({
    queryKey: costBudgetKey(resourceType, resourceId),
    queryFn: () => fetchCostBudget(resourceType, resourceId),
    enabled: enabled && !!resourceId && !!resourceType,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    // On error: return last good value; don't flash banner into error state.
    placeholderData: (prev) => prev,
  });
}
